import { DateTime } from 'luxon'

import {
  formatWithZoneLabel,
  instantInZone,
} from '@sportninja/common/ts-utils/luxon-permanent-dst'

/** 6:00 PM Pacific on Nov 15, 2026, while daylight time is still UTC-7. */
const SAMPLE_INSTANT_UTC = '2026-11-16T01:00:00.000Z'

/** Same token the game row uses for a zone name (`ZZZZ`). */
const ZONE_TIME_FORMAT = 'MMM d, yyyy h:mm a ZZZZ'

/**
 * An instant in `timeZone`, on the offset and label the game rows use.
 * `instantInZone` / `formatWithZoneLabel` are the luxon side of the
 * permanent-DST patch, so a stale runtime does not fall an hour back.
 */
const formatInstant = (iso: string, timeZone: string): string =>
  formatWithZoneLabel(
    instantInZone(iso, timeZone).setLocale('en-US'),
    timeZone,
    ZONE_TIME_FORMAT
  )

/**
 * `Date.getTimezoneOffset()` is minutes to add to local time to reach UTC, so
 * a zone west of UTC is positive. GMT-8 is 480.
 */
export const formatOffsetLabel = (offsetMinutesWest: number): string => {
  if (!Number.isFinite(offsetMinutesWest)) {
    return 'unknown'
  }

  const sign = offsetMinutesWest > 0 ? '-' : '+'
  const absolute = Math.abs(offsetMinutesWest)
  const hours = Math.floor(absolute / 60)
  const minutes = absolute % 60

  if (minutes === 0) {
    return `GMT${sign}${hours}`
  }

  return `GMT${sign}${hours}:${String(minutes).padStart(2, '0')}`
}

/**
 * Plain-text snapshot of what this browser thinks the local timezone is.
 * Nothing is read until the user asks — the panel calls this from a button.
 */
export const collectTimezoneCheck = (now: Date = new Date()): string => {
  let browserTimeZone = ''
  try {
    browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    browserTimeZone = ''
  }

  const offsetMinutes = now.getTimezoneOffset()
  const local = DateTime.fromJSDate(now).setLocale('en-US')
  const zoneName = local.zoneName || browserTimeZone
  const localTime = zoneName
    ? formatInstant(now.toISOString(), zoneName)
    : local.toFormat(ZONE_TIME_FORMAT)
  const sampleInViewer = zoneName
    ? formatInstant(SAMPLE_INSTANT_UTC, zoneName)
    : DateTime.fromISO(SAMPLE_INSTANT_UTC, { zone: 'utc' })
        .toLocal()
        .setLocale('en-US')
        .toFormat(ZONE_TIME_FORMAT)
  const sampleInVancouver = formatInstant(
    SAMPLE_INSTANT_UTC,
    'America/Vancouver'
  )

  const userAgent =
    typeof navigator === 'undefined' ? '' : navigator.userAgent || ''
  const platform =
    typeof navigator === 'undefined' ? '' : navigator.platform || ''

  return [
    'Timezone Check',
    `Generated: ${now.toISOString()}`,
    '',
    `Browser timezone name: ${browserTimeZone || '(not reported)'}`,
    `Offset minutes: ${offsetMinutes}`,
    `Offset: ${formatOffsetLabel(offsetMinutes)}`,
    `Local time: ${localTime}`,
    `Luxon zone: ${local.zoneName || '(none)'}`,
    '',
    `Sample instant (UTC): ${SAMPLE_INSTANT_UTC}`,
    'That instant is 6:00 PM Pacific on Nov 15, 2026 at UTC-7.',
    `Shown in your zone: ${sampleInViewer}`,
    `Shown as America/Vancouver: ${sampleInVancouver}`,
    '',
    `Browser: ${userAgent}`,
    `Platform: ${platform}`,
  ].join('\n')
}
